import { useTranslation } from 'react-i18next';
import SEO from '../../components/seo/SEO';

export default function CookiePolicyPage() {
  const { t } = useTranslation('cookies');

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        canonical="/cookies"
        noindex={true}
      />
      <div className="py-12 bg-gradient-to-br from-primary-50 via-white to-purple-50">
        <div className="container-marketing max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-purple-600">
              {t('title')}
            </span>
          </h1>
          <p className="text-gray-600">{t('last_updated')}</p>
        </div>
      </div>

      <div className="py-12">
        <div className="container-marketing max-w-4xl prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('what_are_cookies.title')}</h2>
            <p className="text-gray-700 leading-relaxed">
              {t('what_are_cookies.content')}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('how_we_use.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('how_we_use.intro')}
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              {t('how_we_use.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('types.title')}</h2>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('types.essential.title')}</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                {t('types.essential.intro')}
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                {t('types.essential.items', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('types.functional.title')}</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                {t('types.functional.intro')}
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                {t('types.functional.items', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('types.analytics.title')}</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                {t('types.analytics.intro')}
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                {t('types.analytics.items', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('types.marketing.title')}</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                {t('types.marketing.intro')}
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                {t('types.marketing.items', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('third_party.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('third_party.intro')}
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              {t('third_party.items', { returnObjects: true }).map((item: any, index: number) => (
                <li key={index}><strong>{item.name}:</strong> {item.purpose}</li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed">
              {t('third_party.outro')}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('managing.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('managing.intro')}
            </p>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('managing.browser_settings.title')}</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                {t('managing.browser_settings.intro')}
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                {t('managing.browser_settings.items', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <p className="text-gray-700">
                <strong>{t('managing.note_label')}:</strong> {t('managing.note')}
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('lifespan.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('lifespan.intro')}
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              {t('lifespan.items', { returnObjects: true }).map((item: any, index: number) => (
                <li key={index}><strong>{item.type}:</strong> {item.description}</li>
              ))}
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('do_not_track.title')}</h2>
            <p className="text-gray-700 leading-relaxed">
              {t('do_not_track.content')}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('updates.title')}</h2>
            <p className="text-gray-700 leading-relaxed">
              {t('updates.content')}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('contact.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('contact.intro')}
            </p>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <p className="text-gray-700"><strong>{t('contact.email_label')}:</strong> {t('contact.email')}</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
